import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, useTheme as useMuiTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Close, FilterAltOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import AppLayout from './components/layout/AppLayout';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
// import GMSummaryPage from './pages/GMSummaryPage';  // Hidden for now
import AllocationsPage from './pages/AllocationsPage';
import FinancePage from './pages/FinancePage';
// import SalesPage from './pages/SalesPage';  // Hidden for now
import HRAnalysisPage from './pages/HRAnalysisPage';
import ProjectionSimulationPage from './pages/ProjectionSimulationPage';
import DateRangePicker from './components/common/DateRangePicker';
import AuthWrapper from './components/common/AuthWrapper';
import SettingsPage from './pages/SettingsPage';
import ProtectedRoute from "../src/components/common/ProtectedRoute";
import Unauthorized from './components/common/Unauthorized';
import POPage from './pages/POPage';
import POAnalysisPage from './pages/POAnalysisPage';
import UploadDataPage from './pages/UploadDataPage';
import LeaveReviewPage from './pages/LeaveReviewPage';
import { fetchActualsThrough } from "./api/upload";
import { useAuth } from "./context/AuthContext";
import AllocationsPage1, { AllocationsPage1Props } from './pages/AllocationsPage1';
import ExecutiveSummaryPage from './pages/ExecutiveSummaryPage';
import ChatWidget from './components/chat/ChatWidget';
import InvoiceCalendarPage from './pages/InvoiceCalendarPage';
import InvoiceProjectionPage from './pages/InvoiceProjectionPage';
import InvoicesPage from './pages/InvoicesPage';
import ConversionRatePage from './pages/ConversionRatePage';
import AgenticAIPage from './pages/AgenticAIPage';

// Modern, professional theme for multi-module application
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1e40af', // Deep blue for trust and professionalism
      light: '#3b82f6',
      dark: '#1e3a8a',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#059669', // Emerald green for success metrics
      light: '#10b981',
      dark: '#047857',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Light slate background
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a', // Dark slate for primary text
      secondary: '#475569', // Medium slate for secondary text
    },
    success: {
      main: '#059669',
      light: '#10b981',
      dark: '#047857',
    },
    error: {
      main: '#dc2626', // Modern red
      light: '#ef4444',
      dark: '#b91c1c',
    },
    warning: {
      main: '#d97706', // Amber for warnings
      light: '#f59e0b',
      dark: '#b45309',
    },
    info: {
      main: '#0ea5e9', // Sky blue for info
      light: '#38bdf8',
      dark: '#0284c7',
    },
    divider: 'rgba(0, 0, 0, 0.06)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      letterSpacing: '-0.025em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '-0.025em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
      letterSpacing: '-0.025em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.5,
      letterSpacing: '-0.025em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.025em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          textTransform: 'none',
          padding: '10px 20px',
          fontSize: '0.875rem',
          letterSpacing: '0.025em',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1e40af',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1e40af',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1e40af',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1e40af',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          borderRight: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: '4px 0 6px -1px rgba(0, 0, 0, 0.1), 2px 0 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(30, 64, 175, 0.08)',
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(30, 64, 175, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(30, 64, 175, 0.16)',
            },
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          padding: '16px 24px',
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f8fafc',
          color: '#0f172a',
          fontSize: '0.875rem',
          letterSpacing: '0.025em',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(30, 64, 175, 0.02)',
          },
          '&:nth-of-type(even)': {
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
});

/** Floating chat; hidden on /agentic-ai (page has its own assistant UI). Must render under Router. */
function AgenticAiChatGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hideChat = location.pathname.startsWith('/agentic-ai');
  return (
    <>
      {!hideChat && <ChatWidget />}
      {children}
    </>
  );
}

function App() {
  const { user } = useAuth();

  // Initialize with "This Fiscal Year" as default (Apr -> Mar)
  const now = new Date();
  const year = now.getFullYear();

  const fiscalStartYear = now.getMonth() >= 3 ? year : year - 1; // Apr (0-indexed 3) -> Mar (0-indexed 2)
  const defaultStartDate = `${fiscalStartYear}-04`;
  // End of current fiscal year is March of next year (fiscalStartYear + 1)
  const defaultEndDate = `${fiscalStartYear + 1}-03`;



  const [startDate, setStartDate] = useState<string | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<string | null>(defaultEndDate);

  const [actualsThroughDate, setActualsThroughDate] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let mounted = true;

    // Only fetch once backend auth is ready (token set via AuthWrapper -> setUser).
    if (!user) return () => {
      mounted = false;
    };

    (async () => {
      try {
        const actual = await fetchActualsThrough();
        if (mounted) setActualsThroughDate(actual);
      } catch {
        // non-blocking; header chip simply won't show
      }
    })();

    const onActualsThroughUpdated = (e: Event) => {
      const ce = e as CustomEvent<string>;
      if (mounted) setActualsThroughDate(ce.detail ?? null);
    };
    window.addEventListener("actualsThroughUpdated", onActualsThroughUpdated);
    return () => {
      mounted = false;
      window.removeEventListener(
        "actualsThroughUpdated",
        onActualsThroughUpdated,
      );
    };
  }, [user]);


  const handleDateRangeChange = (type: 'start' | 'end', date: string | null) => {
    if (type === 'start') {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const handleQuickSelect = (range: string) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (range) {
      case '3months':
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case '6months':
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case 'year':
        // This Fiscal Year (Apr -> Mar): start April 1, end March 31 of next year
        const thisFyStartYear = endDate.getMonth() >= 3 ? endDate.getFullYear() : endDate.getFullYear() - 1;
        startDate.setFullYear(thisFyStartYear);
        startDate.setMonth(3); // April (0-indexed)
        startDate.setDate(1);
        endDate.setFullYear(thisFyStartYear + 1);
        endDate.setMonth(2); // March (0-indexed)
        endDate.setDate(31);
        break;
      case 'lastyear':
        // Last Fiscal Year (Apr -> Mar)
        // If this FY starts in fiscalStartYear, last FY is (fiscalStartYear-1 Apr) -> (fiscalStartYear Mar)
        const thisFiscalStartYear = endDate.getMonth() >= 3 ? endDate.getFullYear() : endDate.getFullYear() - 1;
        startDate.setFullYear(thisFiscalStartYear - 1);
        startDate.setMonth(3); // April
        startDate.setDate(1);
        endDate.setFullYear(thisFiscalStartYear);
        endDate.setMonth(2); // March
        endDate.setDate(31);
        break;
    }

    setStartDate(startDate.toISOString().slice(0, 7));
    setEndDate(endDate.toISOString().slice(0, 7));
  };

  // Compute a friendly label for the currently selected date range
  const getRangeLabel = (start: string | null, end: string | null) => {
    if (!start || !end) return '';
    const dStart = dayjs(`${start}-01`);
    const dEnd = dayjs(`${end}-01`);
    const today = dayjs();

    // Fiscal year assumed: Apr (month=3) -> Mar
    const thisFYStart = (today.month() >= 3 ? today.year(today.year()) : today.year(today.year() - 1)).month(3).startOf('month');
    const thisFYEnd = thisFYStart.add(11, 'month').startOf('month');
    const lastFYStart = thisFYStart.subtract(1, 'year');
    const lastFYEnd = thisFYEnd.subtract(1, 'year');

    // This Fiscal Year
    if (dStart.isSame(thisFYStart, 'month') && (dEnd.isSame(today, 'month') || dEnd.isSame(thisFYEnd, 'month'))) {
      return 'This Fiscal Year';
    }

    // Last Fiscal Year
    if (dStart.isSame(lastFYStart, 'month') && dEnd.isSame(lastFYEnd, 'month')) {
      return 'Last Fiscal Year';
    }

    // This Month
    if (dStart.isSame(today, 'month') && dEnd.isSame(today, 'month')) {
      return 'This Month';
    }

    // Last Month
    if (dStart.isSame(today.subtract(1, 'month'), 'month') && dEnd.isSame(today.subtract(1, 'month'), 'month')) {
      return 'Last Month';
    }

    // Last 3 Months (start = today-3 months start, end = last month)
    if (dStart.isSame(today.subtract(3, 'month').startOf('month'), 'month') && dEnd.isSame(today.subtract(1, 'month'), 'month')) {
      return 'Last 3 Months';
    }

    // Last 6 Months
    if (dStart.isSame(today.subtract(6, 'month').startOf('month'), 'month') && dEnd.isSame(today.subtract(1, 'month'), 'month')) {
      return 'Last 6 Months';
    }

    // Fallback - formatted range
    return `${dStart.format('MMM YYYY')} - ${dEnd.format('MMM YYYY')}`;
  };

  // Wrapper to hide DateRangePicker on specific routes like /uploads
  const InnerLayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const hideDateRoutes = ['/uploads', '/settings', '/agentic-ai'];
    // hide DateRangePicker for any route that starts with one of the hideDateRoutes
    const hideDateRange = hideDateRoutes.some((p) => location.pathname.startsWith(p));

    const muiTheme = useMuiTheme();
    const isSmall = useMediaQuery(muiTheme.breakpoints.down('sm'));

    const headerTitle = useMemo(() => {
      const path = location.pathname;
      if (path === '/') return 'Excecutive Summary';
      if (path === '/delivery-summary') return 'Delivery Summary';
      if (path.startsWith('/projects/details')) return 'Projects Details';
      if (path.startsWith('/projects/gm-summary')) return 'Gross margin';
      if (path.startsWith('/projects')) return 'Delivery';
      if (path.startsWith('/allocations')) return 'Allocations';
      if (path.startsWith('/finance')) return 'Finance overview';
      if (path.startsWith('/sales')) return 'Sales';
      if (path.startsWith('/purchase-orders')) return 'Purchase orders';
      if (path.startsWith('/invoice-calendar')) return 'Invoice Calendar';
      if (path.startsWith('/po-analysis')) return 'Po Analysis';
      if (path.startsWith('/hr-analysis')) return 'HR analysis';
      if (path.startsWith('/projection-simulation')) return 'Projection simulation';
      if (path.startsWith('/uploads')) return 'Uploads';
      if (path.startsWith('/settings')) return 'Settings';
      if (path.startsWith('/invoices')) return 'Invoices';
      if (path.startsWith('/conversion-rate')) return 'Conversion Rate';
      if (path.startsWith('/agentic-ai')) return 'Agentic AI';
      return '';
    }, [location.pathname]);

    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);

    const openFilterDialog = () => {
      setFilterDialogOpen(true);
    };

    const closeFilterDialog = () => {
      setFilterDialogOpen(false);
    };

    const toggleFilterAccordion = () => {
      setFilterAccordionOpen((v) => !v);
    };

    const closeFilterAccordion = () => {
      setFilterAccordionOpen(false);
    };

    const rangeLabel = getRangeLabel(startDate, endDate);

    return (
      <AppLayout
        pageTitle=""
        pageActions={null}
        startDate={startDate}
        endDate={endDate}
        header={
          <>
            <Box
              component="header"
              sx={{
                // Make the header flow with the document so it scrolls with the page
                mb: 2,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 1, sm: 1.25 },
                borderRadius: 2,
                border: '1px solid rgba(226, 232, 240, 0.9)',
                backgroundColor: 'rgba(248, 250, 252, 0.92)',
                // small visual lift but not fixed
                boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{ minHeight: 44 }}
              >
                {/* Left side (title + selected range) */}
                <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ minWidth: 0 }}>
                  {!isSmall && (
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        fontSize: { sm: '1.05rem', md: '1.15rem' },
                        letterSpacing: '-0.02em',
                        color: 'text.primary',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {headerTitle}
                    </Typography>
                  )}

                  {/* Show friendly range label only when date range is not hidden for this route */}
                  {!hideDateRange && (
                    <Typography
                      variant={isSmall ? 'subtitle2' : 'body2'}
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: isSmall ? '70vw' : 'none',
                      }}
                      title={rangeLabel}
                    >
                      {rangeLabel}
                    </Typography>
                  )}

                </Stack>

                {/* Right side (Actuals Through + optional Filter button) */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
                  {actualsThroughDate ? (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`Actuals Through: ${dayjs(actualsThroughDate).format('MMM YYYY')}`}
                      sx={{ fontWeight: 700 }}
                    />
                  ) : null}

                  {/* Hide filter action on routes where date range is hidden */}
                  {!hideDateRange && (
                    <Button
                      onClick={toggleFilterAccordion}
                      variant="contained"
                      startIcon={
                        <FilterAltOutlined
                          sx={{
                            transition: 'transform 180ms ease',
                            transform: filterAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      }
                      sx={{
                        px: 2,
                        minHeight: 40,
                        borderRadius: 2,
                        fontWeight: 700,
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                      }}
                      aria-label="Toggle date filters"
                      aria-expanded={filterAccordionOpen}
                      aria-controls="inline-date-filters"
                    >
                      Filter
                    </Button>
                  )}
                </Stack>
              </Stack>

              {/* Inline accordion panel (replaces modal experience) */}
              {!hideDateRange && (
                <Collapse in={filterAccordionOpen} timeout={180} unmountOnExit>
                  <Box
                    id="inline-date-filters"
                    role="region"
                    aria-label="Date filters panel"
                    sx={{
                      mt: { xs: 1.25, sm: 1.5 },
                      pt: { xs: 1.25, sm: 1.5 },
                      borderTop: '1px solid rgba(226, 232, 240, 0.9)',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                      alignItems: { xs: 'stretch', sm: 'flex-start' },
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        ml: { sm: 'auto' },
                        display: 'flex',
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                      }}
                    >
                      <DateRangePicker
                        key={filterAccordionOpen ? 'open' : 'closed'}
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={(date) => {
                          handleDateRangeChange('start', date);
                        }}
                        onEndDateChange={(date) => {
                          handleDateRangeChange('end', date);
                          closeFilterAccordion();
                        }}
                        onQuickSelect={(range) => {
                          handleQuickSelect(range);
                          closeFilterAccordion();
                        }}
                      />
                    </Box>

                    {/* Quick-select buttons (same ranges as quick select) */}
                    {/* <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button size="small" variant="outlined" onClick={() => { handleQuickSelect('thismonth'); closeFilterAccordion(); }}>
                          This Month
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => { handleQuickSelect('lastmonth'); closeFilterAccordion(); }}>
                          Last Month
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => { handleQuickSelect('3months'); closeFilterAccordion(); }}>
                          Last 3 Months
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => { handleQuickSelect('6months'); closeFilterAccordion(); }}>
                          Last 6 Months
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => { handleQuickSelect('year'); closeFilterAccordion(); }}>
                          This Fiscal Year
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => { handleQuickSelect('lastyear'); closeFilterAccordion(); }}>
                          Last Fiscal Year
                        </Button>
                      </Stack> */}

                    {/* Apply / Close controls */}
                    {/* <Stack direction="row" justifyContent="flex-end" spacing={1}>
                        <Button size="small" variant="outlined" onClick={closeFilterAccordion}>
                          Close
                        </Button>
                        <Button size="small" variant="contained" onClick={closeFilterAccordion}>
                          Apply
                        </Button>
                      </Stack> */}
                  </Box>
                </Collapse>
              )}
            </Box>

            <Dialog
              open={filterDialogOpen}
              onClose={closeFilterDialog}
              aria-labelledby="date-filter-dialog-title"
              fullWidth
              maxWidth="sm"
              transitionDuration={{ enter: 160, exit: 120 }}
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <DialogTitle id="date-filter-dialog-title" sx={{ pr: 6, fontWeight: 800 }}>
                Date Filters
                <IconButton
                  onClick={closeFilterDialog}
                  aria-label="Close date filter dialog"
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <Close />
                </IconButton>
              </DialogTitle>

              <DialogContent dividers sx={{ py: 2 }}>
                <DateRangePicker
                  key={filterDialogOpen ? 'open' : 'closed'}
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={(date) => {
                    handleDateRangeChange('start', date);
                  }}
                  onEndDateChange={(date) => {
                    handleDateRangeChange('end', date);
                    closeFilterDialog();
                  }}
                  onQuickSelect={(range) => {
                    handleQuickSelect(range);
                    closeFilterDialog();
                  }}
                />
              </DialogContent>
            </Dialog>
          </>

        }
      >
        <AgenticAiChatGate>{children}</AgenticAiChatGate>
      </AppLayout>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>

          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* All routes rendered inside main AppLayout */}
          <Route
            path="*"
            element={
              <AuthWrapper>
                {/* Wrapper component to conditionally show/hide DateRangePicker based on location */}
                <InnerLayoutWrapper>
                  <Routes>


                    {/* executive summary */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute moduleName="Dashboard" action="read">
                          <ExecutiveSummaryPage
                            startDate={startDate}
                            endDate={endDate}
                            actualsThroughDate={actualsThroughDate}
                          />
                        </ProtectedRoute>
                      }
                    />


                    {/* Summary */}
                    <Route
                      path="/delivery-summary"
                      element={
                        <ProtectedRoute moduleName="Dashboard" action="read">
                          <DashboardPage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    />

                    {/* POpage */}
                    <Route
                      path="/purchase-orders"
                      element={
                        <ProtectedRoute moduleName="Purchase Orders" action="read">
                          <POPage />
                        </ProtectedRoute>
                      }
                    />
                  {/* Invoice */}
                    <Route
                      path="/invoices"
                      element={
                        <ProtectedRoute moduleName="Purchase Orders" action="read">
                          <InvoicesPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Po Analysis */}
                    <Route
                      path="/po-analysis"
                      element={
                        <ProtectedRoute moduleName="Po Analysis" action="read">
                          <POAnalysisPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Delivery */}
                    <Route
                      path="/projects"
                      element={<Navigate to="/projects/details" replace />}
                    />

                    {/* Projects: Project Details */}
                    <Route
                      path="/projects/details"
                      element={
                        <ProtectedRoute moduleName="Projects" action="read">
                          <ProjectsPage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    />

                    {/* Projects: GM Summary - Hidden for now */}
                    {/* <Route
                      path="/projects/gm-summary"
                      element={
                        <ProtectedRoute moduleName="Projects" action="read">
                          <GMSummaryPage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    /> */}

                    {/* Allocations */}
                    {/* <Route
                      path="/allocations"
                      element={
                        <ProtectedRoute moduleName="Allocations" action="read">
                          <AllocationsPage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    /> */}

                    {/* billed-allocations */}
                    <Route
                      path="/allocations"
                      element={
                        <ProtectedRoute moduleName="Allocations" action="read">
                          <AllocationsPage1 {...({ startDate, endDate } as AllocationsPage1Props)} />
                        </ProtectedRoute>
                      }
                    />
                    {/* Finance */}
                    <Route
                      path="/finance"
                      element={
                        <ProtectedRoute moduleName="Finance" action="read">
                          <FinancePage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    />

                    {/* Sales - Hidden for now */}
                    {/* <Route
                      path="/sales"
                      element={
                        <ProtectedRoute moduleName="Sales" action="read">
                          <SalesPage startDate={startDate || ""} endDate={endDate || ""} />
                        </ProtectedRoute>
                      }
                    /> */}

                    {/* HR Analysis */}
                    <Route
                      path="/hr-analysis"
                      element={
                        <ProtectedRoute moduleName="HR Analysis" action="read">
                          <HRAnalysisPage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    />

                    {/* Projection Simulation (if needs protection add moduleName) */}
                    <Route
                      path="/projection-simulation"
                      element={
                        <ProtectedRoute moduleName="Project and Simulation" action="read">
                          <ProjectionSimulationPage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    />

                    {/* Settings */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute moduleName="Settings" action="read">
                          <SettingsPage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    />

                    {/* Conversion Rate  */}
                    <Route
                      path="/conversion-rate"
                      element={
                        <ProtectedRoute moduleName="Conversion Rate" action="read">
                          <ConversionRatePage startDate={startDate} endDate={endDate} />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/agentic-ai"
                      element={
                        <ProtectedRoute moduleName="AI Agent" action="read">
                          <AgenticAIPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Upload */}
                    <Route path="/uploads" element={
                      <ProtectedRoute moduleName="Uploads" action="read">
                        <UploadDataPage />
                      </ProtectedRoute>} />

                    {/* Leave balance */}
                    <Route path="/leave-balance" element={
                      <ProtectedRoute moduleName="Leave Balance" action="read">
                        <LeaveReviewPage />
                      </ProtectedRoute>} />

                    {/* Invoice calendar */}
                    <Route
                      path="/invoice-calendar"
                      element={
                        <ProtectedRoute moduleName="Invoice Calendar" action="read">
                          <InvoiceCalendarPage />
                        </ProtectedRoute>
                      }
                    />
                      {/* Invoice projection */}
                    <Route
                      path="/invoice-projection"
                      element={
                        <ProtectedRoute moduleName="Invoice Calendar" action="read">
                          <InvoiceProjectionPage />
                        </ProtectedRoute>
                      }
                    />


                    {/* Catch-all inside layout */}
                    <Route path="*" element={<Navigate to="/" replace />} />

                  </Routes>
                </InnerLayoutWrapper>
              </AuthWrapper>
            }
          />

        </Routes>
      </Router>

    </ThemeProvider>
  );
}

export default App;
